import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const kvCacheCompressionComponent: ComponentDef = {
  id: 'kv-cache-compression',
  name: 'KV Cache Compression',
  description: 'Evict unimportant KV cache entries to reduce memory footprint: H2O (heavy-hitter oracle) and SnapKV (prompt clustering).',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Full cache length', type: 'number', default: 1024, min: 64, max: 32768, step: 64 },
    { name: 'budget', label: 'Cache budget (tokens)', type: 'number', default: 256, min: 16, max: 4096, step: 16 },
    {
      name: 'variant',
      label: 'Eviction strategy',
      type: 'select',
      default: 'h2o',
      options: [
        { label: 'H2O (Heavy Hitter Oracle)', value: 'h2o' },
        { label: 'SnapKV (pooled prompt clusters)', value: 'snapkv' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const seq_len = Number(params.seq_len);
    const budget = Number(params.budget);
    const d_head = d_model / n_heads;
    const variant = String(params.variant || 'h2o');

    const g = new GraphBuilder();
    g.input('q', [n_heads, 1, d_head]);          // current query (decode step)
    g.input('k_cache', [n_heads, seq_len, d_head]);
    g.input('v_cache', [n_heads, seq_len, d_head]);

    if (variant === 'h2o') {
      // H2O: track cumulative attention mass per token; evict lowest-mass tokens
      g.transpose('kt', 'k_cache', [n_heads, d_head, seq_len], 'K^T');
      g.matmul('scores', 'q', 'kt', [n_heads, 1, seq_len]);
      g.scale('scaled', 'scores', '1/√d_k');
      g.softmax('attn_w', 'scaled', -1);
      // Accumulate attention mass: hit_score[i] += attn_w[i]
      g.activation('hit_scores', 'attn_w', 'Σ attn_w  (cumulative hit score per token)');
      // Keep top-budget tokens by hit score; always retain recent window
      g.router('keep_mask', 'hit_scores', [n_heads, budget, 1]);
      g.reshape('k_evicted', 'keep_mask', [n_heads, budget, d_head]);
      g.reshape('v_evicted', 'keep_mask', [n_heads, budget, d_head]);
      // Attention over compressed cache
      g.transpose('kt2', 'k_evicted', [n_heads, d_head, budget], 'K^T (compressed)');
      g.matmul('scores2', 'q', 'kt2', [n_heads, 1, budget]);
      g.scale('scaled2', 'scores2', '1/√d_k');
      g.softmax('attn_w2', 'scaled2', -1);
      g.matmul('out', 'attn_w2', 'v_evicted', [n_heads, 1, d_head]);

    } else {
      // SnapKV: pool observation window attention to cluster important prompt tokens
      const obs_window = Math.min(16, seq_len);
      g.reshape('obs_q', 'q', [n_heads, obs_window, d_head]);
      g.transpose('kt', 'k_cache', [n_heads, d_head, seq_len], 'K^T');
      g.matmul('obs_scores', 'obs_q', 'kt', [n_heads, obs_window, seq_len]);
      g.scale('obs_scaled', 'obs_scores', '1/√d_k');
      g.softmax('obs_attn', 'obs_scaled', -1);
      // Pool attention across observation window → importance score per prompt token
      g.activation('importance', 'obs_attn', 'mean over obs window  (pooled importance)');
      // Select top-budget tokens via clustering
      g.router('snap_mask', 'importance', [n_heads, budget, 1]);
      g.reshape('k_snap', 'snap_mask', [n_heads, budget, d_head]);
      g.reshape('v_snap', 'snap_mask', [n_heads, budget, d_head]);
      // Decode attention over compressed cache
      g.transpose('kt2', 'k_snap', [n_heads, d_head, budget], 'K^T (compressed)');
      g.matmul('scores2', 'q', 'kt2', [n_heads, 1, budget]);
      g.scale('scaled2', 'scores2', '1/√d_k');
      g.softmax('attn_w2', 'scaled2', -1);
      g.matmul('out', 'attn_w2', 'v_snap', [n_heads, 1, d_head]);
    }

    g.output('out', 'out');
    return g.build();
  }
};
